/**
 * 値型のコピー（labs.md ラボ 3 手順 4、components.md §3.2「コピーして操作」、/compute/04-inout/）。
 * 配列 `a` を `var b = a` で写し、片方だけ操作して、もう片方が変わらないことを 2 つの棚で見せる。
 */
import type { ApplyResult, LabModel } from './types';
import { swiftArray } from './swift-format';

export type ShelfName = 'a' | 'b';

export interface ShelvesState {
  readonly a: readonly number[];
  /** `var b = a` の前は null。 */
  readonly b: readonly number[] | null;
}

export type ShelvesOp =
  | { type: 'copy' }
  | { type: 'append'; target: ShelfName; value: number }
  | { type: 'removeLast'; target: ShelfName };

export const INITIAL_SHELVES: ShelvesState = { a: [1, 2, 3], b: null };

function withShelf(state: ShelvesState, target: ShelfName, items: readonly number[]): ShelvesState {
  return target === 'a' ? { a: items, b: state.b } : { a: state.a, b: items };
}

function apply(state: ShelvesState, op: ShelvesOp): ApplyResult<ShelvesState> {
  if (op.type === 'copy') {
    if (state.b !== null) {
      return {
        state,
        entry: null,
        moved: 0,
        message: 'b はもう作ってあります。最初からにすると、もう一度写せます。',
      };
    }
    return {
      state: { a: state.a, b: [...state.a] },
      entry: { code: 'var b = a', tag: '写す', changed: ['b'] },
      moved: state.a.length,
      message: `a の中身 ${state.a.length} 個を、別の棚 b に写しました。`,
    };
  }
  const shelf = op.target === 'a' ? state.a : state.b;
  if (shelf === null) {
    return { state, entry: null, moved: 0, message: '先に「var b = a」で b を作ります。' };
  }
  if (op.type === 'append') {
    const other = op.target === 'a' ? 'b' : 'a';
    return {
      state: withShelf(state, op.target, [...shelf, op.value]),
      entry: { code: `${op.target}.append(${op.value})`, tag: '追加', changed: [op.target] },
      moved: 1,
      message:
        state.b === null
          ? `a に ${op.value} を足しました。`
          : `${op.target} に ${op.value} を足しました。${other} は変わりません。`,
    };
  }
  const last = shelf.at(-1);
  if (last === undefined) {
    return {
      state,
      entry: null,
      moved: 0,
      message: `${op.target} は空です。空の配列の removeLast() は実行時エラーになります。`,
    };
  }
  return {
    state: withShelf(state, op.target, shelf.slice(0, -1)),
    entry: {
      code: `${op.target}.removeLast()`,
      result: String(last),
      tag: '取り出し',
      changed: [op.target],
    },
    moved: 1,
    message: `${op.target} の末尾の ${last} を取り出しました。`,
  };
}

export const shelvesModel: LabModel<ShelvesState, ShelvesOp> = {
  id: 'copy',
  apply,
  declarations: (initial) => [`var a = ${swiftArray(initial.a)}`],
  labels: (initial) => (initial.b === null ? ['a'] : ['a', 'b']),
  vizLine: (_state, label) => `viz.show(${JSON.stringify(label)}, ${label})`,
  snapshot: (state, label) => ({
    kind: 'array',
    value: [...(label === 'a' ? state.a : (state.b ?? []))],
  }),
};

/** 2 つの棚を print した 1 行（`[1, 2, 3] [1, 2, 3, 4]`）。 */
export function printShelves(state: ShelvesState): string {
  return state.b === null ? swiftArray(state.a) : `${swiftArray(state.a)} ${swiftArray(state.b)}`;
}
